import { forwardRef, useCallback } from "react";
import { Link, LinkProps } from "react-router-dom";

/**
 * Route → dynamic import map. Mirrors the lazy() declarations in App.tsx so
 * hovering / focusing a link warms the chunk cache before navigation. Each
 * value is a function returning a Promise so prefetching is opt-in per route.
 */
const PREFETCH_MAP: Record<string, () => Promise<unknown>> = {
  "/about": () => import("@/pages/About"),
  "/services": () => import("@/pages/Services"),
  "/methodology": () => import("@/pages/Methodology"),
  "/compliance": () => import("@/pages/Compliance"),
  "/references": () => import("@/pages/References"),
  "/gallery": () => import("@/pages/Gallery"),
  "/tenders": () => import("@/pages/Tenders"),
  "/contact": () => import("@/pages/Contact"),
  "/faq": () => import("@/pages/FAQ"),
  "/quote-request": () => import("@/pages/QuoteRequest"),
  "/news": () => import("@/pages/News"),
  "/appointment": () => import("@/pages/Appointment"),
  "/documents": () => import("@/pages/Documents"),
  "/careers": () => import("@/pages/Careers"),
  "/partners": () => import("@/pages/Partners"),
  "/branding": () => import("@/pages/Branding"),
  "/legal-notice": () => import("@/pages/LegalNotice"),
  "/privacy-policy": () => import("@/pages/PrivacyPolicy"),
};

const prefetched = new Set<string>();

function prefetch(to: string) {
  if (prefetched.has(to)) return;
  // Try exact match, then first segment (e.g. /news/foo → /news).
  const key = PREFETCH_MAP[to]
    ? to
    : Object.keys(PREFETCH_MAP).find((k) => to.startsWith(k + "/"));
  if (!key) return;
  prefetched.add(to);
  PREFETCH_MAP[key]?.().catch(() => prefetched.delete(to));
}

/**
 * Drop-in replacement for react-router's Link that triggers the lazy chunk
 * for the destination route on hover/focus. Reduces perceived navigation
 * latency without affecting the critical rendering path.
 */
const PrefetchLink = forwardRef<HTMLAnchorElement, LinkProps>(
  ({ to, onMouseEnter, onFocus, onTouchStart, ...rest }, ref) => {
    const target = typeof to === "string" ? to : to.pathname ?? "";

    const handleMouseEnter = useCallback(
      (e: React.MouseEvent<HTMLAnchorElement>) => {
        prefetch(target);
        onMouseEnter?.(e);
      },
      [target, onMouseEnter],
    );

    const handleFocus = useCallback(
      (e: React.FocusEvent<HTMLAnchorElement>) => {
        prefetch(target);
        onFocus?.(e);
      },
      [target, onFocus],
    );

    const handleTouchStart = useCallback(
      (e: React.TouchEvent<HTMLAnchorElement>) => {
        prefetch(target);
        onTouchStart?.(e);
      },
      [target, onTouchStart],
    );

    return (
      <Link
        ref={ref}
        to={to}
        onMouseEnter={handleMouseEnter}
        onFocus={handleFocus}
        onTouchStart={handleTouchStart}
        {...rest}
      />
    );
  },
);

PrefetchLink.displayName = "PrefetchLink";

export default PrefetchLink;
